import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import PageHeader from '@features/PageHeader';
import Button from '@components/Button';
import { ModalConfirm } from '@components/Modal';
import { CampaignsIcon } from 'assets/images/icons';
import { useTranslation } from '@hooks';
import {
    campaignsActions,
    alertActions,
    channelsActions,
    surveysActions,
    labelsActions,
    couponsActions,
} from '@actions';
import { campaignsService } from '@services';
import { ModalType } from '@constants/modal';
import OneTimeTable from './OneTimeTable';
import OngoingTable from './OngoingTable';
import EditModal from './EditModal';
import { CAMPAIGN_TYPE } from '@constants/campaigns';
import { RoutePath } from '@constants/routes';
import { handleApiError } from '@helpers/api';
import { ROLE } from '@constants/user';

const Container = styled('div')({
    '@media (max-width: 1024px)': {
        padding: 16,
        height: 'calc(var(--100vh) - 108px)',
        overflowY: 'auto',
    },
});

const Campaigns = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const PAGE_TYPE =
        location.pathname === RoutePath.CampaignsAutomated ? CAMPAIGN_TYPE.ONGOING : CAMPAIGN_TYPE.ONETIME;
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [modal, setModal] = useState({ open: false, type: ModalType.Create });

    const companies = useSelector(state => state.account?.account?.companies || []);
    const brands = useSelector(state => state.brands.data);
    const user = useSelector(state => state.auth.user);
    const selectedCampaignRef = useRef({});
    const dispatch = useDispatch();

    useEffect(() => {
        async function fetchData() {
            dispatch(channelsActions.get());
            dispatch(channelsActions.getWhatsAppBusinessTemplates());
            if (PAGE_TYPE === CAMPAIGN_TYPE.ONGOING) {
                dispatch(surveysActions.getAll());
            }
            dispatch(campaignsActions.get(0, 10, PAGE_TYPE));
            dispatch(labelsActions.get());
            dispatch(couponsActions.getAll());
        }
        setPage(0);
        setRowsPerPage(10);
        setModal({ open: false, type: ModalType.Create });
        fetchData();
    }, [dispatch, location]);

    const onClickCampaign = (campaign, type) => {
        setSelectedCampaign(campaign);
        selectedCampaignRef.current = campaign;
        switch (type) {
            case 'delete':
                setDeleteModalOpen(true);
                break;
            case 'edit':
                setModal({ open: true, type: ModalType.Edit });
                break;
            case 'view':
                setModal({ open: true, type: ModalType.View });
                break;
            case 'pause':
                onModifyCampaign('pause');
                break;
            case 'enable':
                onModifyCampaign('enable');
                break;
            default:
                break;
        }
    };

    const campaignStateMachine = {
        delete: {
            callback: campaignsService.deleteCampaign,
            withModal: true,
            onSuccess: () => {
                setDeleteModalOpen(false);
                if (modal.type !== ModalType.Create) {
                    setModal({ open: false, type: ModalType.Create });
                }
            },
        },
        pause: { callback: campaignsService.pauseCampaign, withModal: false },
        enable: { callback: campaignsService.enableCampaign, withModal: false },
    };

    const onModifyCampaign = async operation => {
        try {
            await campaignStateMachine[operation].callback(selectedCampaignRef.current?.id);
            if (campaignStateMachine[operation].withModal) {
                campaignStateMachine[operation].onSuccess();
            }
            await dispatch(campaignsActions.get(page, rowsPerPage, PAGE_TYPE));
            dispatch(alertActions.success(t(`alertMessages.${operation}Success`)));
        } catch (error) {
            handleApiError(error, `alertMessages.${operation}Fail`);
        }
        setSelectedCampaign(null);
        selectedCampaignRef.current = {};
    };

    return (
        <Container>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<CampaignsIcon />}
                title={t('CampaignsPage.title')}
                subtitle={t('CampaignsPage.subtitle')}
            >
                {user.role !== ROLE.USER && (
                    <Button variant="contained" onClick={() => setModal({ open: true, type: ModalType.Create })}>
                        {t('buttons.createCampaign')}
                    </Button>
                )}
            </PageHeader>
            {PAGE_TYPE === CAMPAIGN_TYPE.ONETIME ? (
                <OneTimeTable
                    companies={companies}
                    brands={brands}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    setPage={setPage}
                    setRowsPerPage={setRowsPerPage}
                    onClickCampaign={onClickCampaign}
                />
            ) : (
                <OngoingTable
                    companies={companies}
                    brands={brands}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    setPage={setPage}
                    setRowsPerPage={setRowsPerPage}
                    onClickCampaign={onClickCampaign}
                />
            )}
            {modal.open ? (
                <EditModal
                    modal={modal}
                    setModal={setModal}
                    campaignType={PAGE_TYPE}
                    brands={brands}
                    selectedCampaign={selectedCampaign}
                    setSelectedCampaign={setSelectedCampaign}
                    rowsPerPage={rowsPerPage}
                    onDelete={campaign => {
                        selectedCampaignRef.current = campaign;
                        setDeleteModalOpen(true);
                    }}
                />
            ) : null}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => setDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={() => onModifyCampaign('delete')}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                    onSecondaryAction={() => setDeleteModalOpen(false)}
                />
            )}
        </Container>
    );
};

export default Campaigns;
